import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import { useWeb3React } from "@web3-react/core";
import {
	Row,
	Col,
	Button,
	Label,
	Card,
	CardBody,
	Input as InputStrap,
} from "reactstrap";
import { styled } from "@mui/system";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Input from "@mui/material/Input";
import Typography from "@mui/material/Typography";
import { Field, Formik, Form } from "formik";
import { ethers } from "ethers";
import { formatEther } from "@ethersproject/units";

import { tmpDelete, tmpUpload } from "../../store/actions/tmpUpload";
import collectionIcon from "../../assets/img/collection.png";
import ContractModal from "./ContractModal";
import ConfirmModal from "./ConfirmModal";
import {
	useContractFormDataByType,
	pinJSONIPFS,
	pinFileIPFS,
} from "../../hooks";
import { getContractFactoryAbi } from "../../utils";
import { saveContract } from "../../store/actions/contract";
import bn from "bignumber.js";

const ImageCollection = styled("input")({
	display: "none",
});

const DEFAULT_DECIMALS = new bn(10).pow(18);

const parseDataToDeployArgs = (data, type) => {
	switch (type) {
		case "ERC721":
			return [
				data.name,
				data.symbol,
				"ipfs://ipfs/",
				data.meta,
				data.maxSupply ?? 0,
				data.saleDuration ?? 0,
				// "0x0000000000000000000000000000000000000000000000000000000000000000",
			];
		case "ERC721A":
		case "ERC721PSI":
			return [
				data.name,
				data.symbol,
				data.maxSupply ?? 0,
				"https://gateway.pinata.cloud/ipfs/",
				data.meta,
				data.saleDuration ?? 0,
				// "0x0000000000000000000000000000000000000000000000000000000000000000",
			];
		case "ERC1155":
			return [
				data.name,
				data.symbol,
				"ipfs://ipfs/",
				data.meta,
				data.maxSupply ?? 0,
			];
		case "ERC20":
			return [
				data.name,
				data.symbol,
				data.maxSupply
					? new bn(data.maxSupply).times(DEFAULT_DECIMALS).toFixed()
					: 0,
			];
		default:
			return [];
	}
};

const ContractNew = ({ masterId }) => {
	const { account, chainId, library } = useWeb3React();

	const history = useHistory();

	const [show, setShow] = useState(false);
	const [stage, setStage] = useState("none");
	const [error, setError] = useState(null);
	const [ownPinata, setOwnPinata] = useState(false);
	const [openActionModal, setOpenActionModal] = useState(false);

	const handleCloseActionModal = useCallback(() => {
		setOpenActionModal(false);
	}, [setOpenActionModal]);

	const dispatch = useDispatch();

	const {
		user,
		user: { isOwnPinata },
		user: { userPinataAPIKey },
		user: { userPinataAPISecret },
	} = useSelector((state) => state.user);

	const { image } = useSelector((state) => state.tmpUpload);
	const { templates } = useSelector((state) => state.contract);
	const templateContract = useMemo(() => {
		return templates.find((template) => template._id === masterId);
	}, [templates, masterId]);
	const {
		schema: userSchema,
		getInitialValues,
		formFieldsData,
	} = useContractFormDataByType(templateContract?.type);

	const handleUploadImg = (event) => {
		let file = event.target.files[0];
		dispatch(tmpUpload(file));
		file = "";
	};

	const handleChange = (event) => {
		if (event.target.name === "isOwnPinata") {
			setOwnPinata(event.target.checked);
		}
	};

	const hideModal = () => {
		return new Promise((resolve) => {
			setTimeout(() => {
				setShow(false);
				setStage("none");
				setError(null);
				resolve();
			}, 3000);
		});
	};

	// useEffect(() => {
	// 	console.log(history);
	// }, [history]);

	const onFormSubmit = async (data) => {
		// let type = action;
		setShow(true);
		setStage("read");

		let metadata = { ...data };
		metadata.fee_recipient = "";
		metadata.external_link = "";
		metadata.seller_fee_basis_points = data.royalties * 100;
		metadata.fee_recipient = "";

		async function hashCreator(jsonData) {
			if (image.name) {
				const { imageHash } = await pinFileIPFS(
					image.name,
					null,
					null,
					"tmp",
					ownPinata ? user._id : "main"
				);
				data.image = imageHash;
				jsonData.image = "ipfs://ipfs/" + imageHash;
			} else {
				jsonData.image = "ipfs://ipfs/" + templateContract.image;
				data.image = templateContract.image;
			}
			const hashMeta = await pinJSONIPFS(jsonData);
			return hashMeta;
		}

		const factory = new ethers.Contract(
			templateContract.address,
			getContractFactoryAbi(templateContract.type),
			library ? library.getSigner() : library
		);

		const hashMetaContract = await hashCreator(metadata);
		data.meta = hashMetaContract;
		setStage("sign");

		const deployArgs = parseDataToDeployArgs(data, templateContract.type);

		const gas = await factory.estimateGas
			.deploy(...deployArgs)
			.catch(() => "720000");

		try {
			const contract = await factory
				.deploy(...deployArgs, { gasLimit: gas })
				.catch(() => {
					return null;
				});

			if (contract === null) {
				await hideModal();
				return;
			}

			setStage("deploy");
			const depcontract = await contract.wait();
			const address = depcontract.events.find(
				(e) =>
					e.topics[0] ===
					"0xcf78cf0d6f3d8371e1075c69c492ab4ec5d8cf23a1a239b6a51a1d00be7ca312"
			);
			data.masterContract = templateContract._id;
			data.address = address.args[0];
			data.type = templateContract.type;
			data.user = user._id;
			data.chain = chainId;
			data.gas = (
				depcontract.effectiveGasPrice.toNumber() *
				parseFloat(formatEther(depcontract.gasUsed._hex))
			).toFixed(8);

			dispatch(saveContract(data));
			setStage("done");
			await hideModal();
			history.push(`/contracts/last`);
		} catch (error) {
			console.log("WOW< ERROR ACTUALLY CATCHED", error);
			setError({ message: error.message });
			await hideModal();
		} finally {
			if (image.name) {
				dispatch(tmpDelete(image.name));
			}
		}
	};

	return (
		<>
			<Row>
				{templateContract && (
					<Col md={3}>
						<Card className="ecommerce-card" key={templateContract.name}>
							<CardBody>
								<div className="item-img text-center mx-auto mb-4">
									<Link to={`/library/nft/${templateContract.type}`}>
										<img
											className="img-fluid card-img-top"
											src={
												templateContract.image.includes("https://")
													? templateContract.image
													: `${process.env.REACT_APP_IPFS_HOST}/${
															templateContract?.customImage ??
															templateContract.image
													  }`
											}
											alt={
												templateContract?.customName ?? templateContract.name
											}
										/>
									</Link>
								</div>
								<h6 className="item-name">
									<Link
										className="text-body"
										to={`/library/nft/${templateContract.id}`}
									>
										{templateContract?.customName ?? templateContract.name}
									</Link>
								</h6>
							</CardBody>
						</Card>
					</Col>
				)}
				<Col>
					<Card>
						<CardBody>
							<Row>
								<Col md={3} className="user-avatar-section me-3">
									<div className="d-flex align-items-center flex-column">
										<img
											height="200"
											width="200"
											alt="user-avatar"
											src={
												image?.name
													? `${process.env.REACT_APP_API_DO_URL}/${image.name}`
													: collectionIcon
											}
											className="img-fluid mb-2"
											style={{ borderRadius: "16px" }}
										/>
									</div>
								</Col>
								<Col>
									<h1>Deploy contract</h1>
									<div>
										<ImageCollection
											id="icon-button-file"
											accept="image/png, image/jpeg, image/gif"
											type="file"
											onChange={handleUploadImg}
										/>
										<Label for="icon-button-file">
											<Button
												color="primary"
												aria-label="upload picture"
												tag="span"
											>
												Choose file
											</Button>
										</Label>
										<p>Upload an image of at least 400x400</p>
									</div>
									<Tooltip
										title={
											!userPinataAPIKey || !userPinataAPISecret
												? "Please specify your Pinata API Gateway credentials in your profile to use this feature"
												: ""
										}
									>
										<div>
											{isOwnPinata && (
												<div className="d-flex justify-content-left align-items-center mb-3">
													<Label
														htmlFor="isOwnPinata"
														style={{
															cursor: "pointer",
															fontSize: "14px",
														}}
													>
														Use my Pinata
													</Label>
													<div className="form-switch mb-2 ps-0">
														<InputStrap
															style={{
																marginLeft: "8px",
																marginTop: 0,
																width: "45px",
																cursor: "pointer",
															}}
															type="switch"
															name="isOwnPinata"
															checked={ownPinata}
															disabled={
																!userPinataAPIKey || !userPinataAPISecret
															}
															onChange={handleChange}
															id="isOwnPinata"
															aria-label="controlled"
														/>
													</div>
												</div>
											)}
										</div>
									</Tooltip>

									{ownPinata && (
										<Typography
											sx={{
												mb: "2rem",
												letterSpacing: "0.00938em",
												color: "#808080",
												fontFamily: "Montserrat",
												fontSize: "12px",
												lineHeight: "26px",
											}}
										>
											Metadata of your contract will be uploaded on IPFS using
											your Pinata API Gateway credentials. To review API
											credentials please go to your profile page
										</Typography>
									)}
								</Col>
							</Row>
							<Row>
								<Formik
									initialValues={getInitialValues()}
									validateOnBlur
									validationSchema={userSchema}
									onSubmit={onFormSubmit}
									enableReinitialize={true}
								>
									<Form sx={{ width: "100%" }}>
										<ConfirmModal
											open={openActionModal}
											handleClose={handleCloseActionModal}
										/>
										<Row className="gy-1 pt-75">
											{formFieldsData.map((formField) => (
												<Col key={formField.id} md={6} xs={12}>
													<Field name={formField.name} type={formField.type}>
														{({ field, meta }) => (
															<div
																style={{
																	minWidth: "20%",
																	paddingRight: "1rem",
																	marginBottom: "2rem",
																}}
															>
																<Label
																	className="form-label"
																	for={formField.id}
																>
																	{formField.label}
																</Label>
																<Input
																	{...field}
																	{...(formField.input ?? [])}
																	id={formField.id}
																	// type={formField.type}
																	inputComponent="input"
																	variant="standard"
																	fullWidth
																/>
																{meta.touched && meta.error && (
																	<div
																		className="field-tip"
																		style={{
																			color: "red",
																		}}
																	>
																		{meta.error}
																	</div>
																)}
																{formField.tip && (
																	<span className="field-tip">
																		{formField.tip}
																	</span>
																)}
															</div>
														)}
													</Field>
												</Col>
											))}
										</Row>
										<Box
											sx={{
												display: "flex",
												gap: "2rem",
												flexWrap: "nowrap",
											}}
										></Box>
										<Box
											sx={{
												padding: "1.5rem",
												width: "100%",
												textAlign: "end",
											}}
										>
											{!account ? (
												<p>Please connect wallet to create collection</p>
											) : (
												<Box
													sx={{
														width: "100%",
														display: "flex",
														justifyContent: "center",
														columnGap: "2rem",
													}}
												>
													<Button
														color="primary"
														variant="outlined"
														className="btn-cart move-cart"
														onClick={() => setOpenActionModal(true)}
													>
														Deploy copy
													</Button>
												</Box>
											)}
										</Box>
									</Form>
								</Formik>
							</Row>
						</CardBody>
					</Card>
				</Col>
			</Row>
			<ContractModal show={show} stage={stage} error={error} />
		</>
	);
};

export default ContractNew;
